<template>
  <Card v-if="customFieldsList.length > 0">
    <CardHeader class="flex justify-between items-center">
      <CardTitle>Custom Fields</CardTitle>
      <Button variant="card-header-action" size="sm" @click="openEditSheet">Edit</Button>
    </CardHeader>
    <CardContent class="relative">
      <dl class="grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2">
        <div
          v-for="customField in visibleCustomFields"
          :key="customField.id"
          :class="[customField.field_type == 'rich_text' ? 'sm:col-span-2' : 'sm:col-span-1', 'space-y-2']"
        >
          <dt class="text-xs font-medium uppercase tracking-[0.08em] text-gray-500">
            {{ customField.name }}
          </dt>
          <dd class="min-h-6">
            <ControlledCustomField
              :custom-field="customField"
              :custom-field-value="person.customFields.find(f => f.custom_field_id === customField.id)"
              custom-fieldable-type="Contact"
              :custom-fieldable-id="person.id"
              mode="view"
            />
          </dd>
        </div>
      </dl>
      <div
        v-if="showGradient"
        class="absolute bottom-12 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none"
      ></div>
      <div v-if="hasMore" class="mt-6 text-center">
        <Button v-if="!isExpanded" variant="link" @click="isExpanded = true"> View all ({{ totalCount }}) </Button>
        <Button v-else variant="link" @click="isExpanded = false">Show less</Button>
      </div>
    </CardContent>
  </Card>

  <EditCustomFieldsSheet
    ref="editSheet"
    v-model:open="isSheetOpen"
    :person="person"
    :custom-fields-list="customFieldsList"
    @saved="emit('customFieldsUpdated')"
  />
</template>

<script setup lang="ts">
import type { Person } from '../Person';
import type { CustomFieldEntity } from '@/api/entity_types/CustomField';
import { computed, ref, toRef } from 'vue';
import Card from '@/components/ui/card/Card.vue';
import CardContent from '@/components/ui/card/CardContent.vue';
import { ControlledCustomField } from '@/components/custom_fields/ControlledCustomField';
import EditCustomFieldsSheet from './EditCustomFieldsSheet.vue';
import CardHeader from '@/components/ui/card/CardHeader.vue';
import CardTitle from '@/components/ui/card/CardTitle.vue';
import { Button } from '@/components/ui/button';
import { useExpandableList } from './useExpandableList';

type Props = {
  person: Person;
  customFieldsList: CustomFieldEntity[];
};
const props = defineProps<Props>();

const emit = defineEmits<{
  customFieldsUpdated: [];
}>();

const isSheetOpen = ref(false);
const editSheet = ref<InstanceType<typeof EditCustomFieldsSheet> | null>(null);

const showGradient = computed(() => !isExpanded.value && hasMore.value);

const {
  isExpanded,
  visibleItems: visibleCustomFields,
  hasMore,
  totalCount,
} = useExpandableList({ items: toRef(props, 'customFieldsList'), limit: 2 });

const openEditSheet = () => {
  editSheet.value?.clearPendingChanges();
  isSheetOpen.value = true;
};
</script>
